import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Calendar, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import { usePagination } from '../../hooks/usePagination';

export function Cashflow() {
  const { 
    data: cashflowEntries, 
    loading, 
    loadingMore,
    error, 
    hasMore, 
    loadMore, 
    fetchData,
    pagination
  } = usePagination('finance/{orgId}/cashflow', { initialLimit: 15 });

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  // Transform cashflow entries for display
  const transformedEntries = cashflowEntries.map(entry => ({
    id: entry.id,
    type: entry.amount >= 0 ? 'income' : 'expense',
    description: entry.description,
    amount: entry.amount,
    date: entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-GB') : 'N/A',
    category: entry.pot_type || 'General'
  }));

  const totalIncome = transformedEntries.filter(i => i.type === 'income').reduce((sum, i) => sum + i.amount, 0);
  const totalExpenses = Math.abs(transformedEntries.filter(i => i.type === 'expense').reduce((sum, i) => sum + i.amount, 0));

  return (
    <div className="page">
      {/* Back Link */}
      <Link to="/finance" className="back-link">
        <ArrowLeft size={16} />
        Back to Finance
      </Link>

      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Cashflow Timeline</h1>
          <p>Track money in and out of your business</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary">
            <Calendar size={16} />
            This Month
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Summary Cards */}
          <div className="responsive-grid-2" style={{ marginBottom: 'var(--space-xl)' }}>
            <GlassPanel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--status-success-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--status-success)'
                }}>
                  <TrendingUp size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Income</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-success)' }}>£{totalIncome.toFixed(2)}</div>
                </div>
              </div>
            </GlassPanel>

            <GlassPanel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--status-danger-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--status-danger)'
                }}>
                  <TrendingDown size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Expenses</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-danger)' }}>£{totalExpenses.toFixed(2)}</div>
                </div>
              </div>
            </GlassPanel>
          </div>

          {/* Timeline */}
          <GlassPanel title="Recent Activity" titleIcon={TrendingUp} className="mb-xl">
            {transformedEntries.length > 0 ? (
              <>
                <div className="cashflow-timeline" data-testid="cashflow-list">
                  {transformedEntries.map((item) => (
                    <div key={item.id} className="cashflow-item" data-testid={`cashflow-item-${item.id}`}>
                      <div className={`cashflow-icon ${item.type}`}>
                        {item.type === 'income' ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                      </div>
                      <div className="cashflow-details">
                        <span className="cashflow-description">{item.description}</span>
                        <span className="cashflow-date">{item.date} • {item.category}</span>
                      </div>
                      <span className={`cashflow-amount ${item.amount > 0 ? 'positive' : 'negative'}`}>
                        {item.amount > 0 ? '+' : ''}£{Math.abs(item.amount).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn btn-secondary"
                      data-testid="load-more-cashflow"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Load More ({pagination.total - transformedEntries.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: 'var(--space-md)',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)'
                }}>
                  Showing {transformedEntries.length} of {pagination.total} entries
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
                <TrendingUp size={48} style={{ marginBottom: 'var(--space-md)', opacity: 0.5 }} />
                <p>No cashflow entries yet</p>
                <p style={{ fontSize: '0.875rem' }}>Entries will appear here when transactions are recorded</p>
              </div>
            )}
          </GlassPanel>
        </>
      )}
    </div>
  );
}

export default Cashflow;
